import * as GLOBALS from './globals';

import EventDispatcher from './events/EventDispatcher';

import { AssetsLibrary } from './media';
import { AudioMixer } from './media/audio';

import { DOMLayout } from './layout';

import Stage from './Stage';
import { Tween } from './animation';
import Ticker from './Ticker';

import ExternalAPI from './external/ExternalAPI';

let INSTANCE = null;
let fpsDivId = "p2m-fps";

/**
 * @class
 * @inheritDoc
 * @classdesc Основной класс приложения
 */
class Application extends EventDispatcher {

    /**
     * Singleton
     * @returns {Application}
     */
    static getInstance() {
        if (!GLOBALS.APP) {
            throw new Error('You must create application instance first.');
        }
        return GLOBALS.APP;
    }

    /**
     * @constructor
     * @param {string} id Уникальный идентификатор приложения
     * @param {Object} config Конфигурация
     * @extends EventDispatcher
     * @inheritDoc
     */
    constructor(id, config = {}, external = null) {

        if (GLOBALS.APP) {
            return GLOBALS.APP;
        }

        super();

        /**
         * Уникальный идентификатор приложения
         * @type {string}
         */
        this.id = id;

        /**
         * Конфигурация
         * @type {Object}
         */
        this.config = config;

        /**
         * Основной тикер приложения
         * @type {Ticker}
         */
        this.ticker = Ticker;

        /**
         * Библиотека изображений
         * @type {AssetsLibrary}
         */
        this.library = null;

        /**
         * Аудио микшер
         * @type {AudioMixer}
         */
        this.audio = new AudioMixer(config.audio);

        /**
         * DOM
         * @type {DOMLayout}
         */
        this.layout = null;

        /**
         * Внешнее API для партнёрской интеграции
         * @type {ExternalAPI}
         */
        this.external = external || new ExternalAPI;

        /**
         * Хранилище данных
         * @type {LocalStorage}
         */
        this.storage = null;

        this.stages = [];

        this._fps = 0;
        this._lastFPS = 0;
        this._fpsClearTime = 0;

        this._showFPS = false;

        GLOBALS.setApplication(this);
    }

    init(container) {
        this.createLayout(container);

        this.detectBitmapScale();
        this.library = new AssetsLibrary('assets/images', this.layout.bitmapScale);

        this.addStage();

        if (this.layout.autoFit) this.layout.fitLayout(true);
        this.layout.showPreloader();

        this.layout.on('hidewindow', this.pauseAllSounds, this);
        this.layout.on('showwindow', this.resumeAllSounds, this);

        /**
         * Окончание инициализации
         * @event Application#ready
         */
        this.external.init(() => this.emit("ready"));
    }

    pauseAllSounds() {
        this.audio.pause();
    }

    resumeAllSounds() {
        this.audio.resume();
    }

    detectBitmapScale() {
        this.layout.detectBitmapScale(this.config.scales);
    }

    createLayout(container) {

        this.layout = new DOMLayout(container, this.config.size, this.config.noCheckOrientation);

        this.layout.on('orientationchange', this.handleOrientationChange, this);

        this.layout.addMobileListeners();
        this.layout.autoFit = true;

        return this.layout;
    }

    handleOrientationChange(e) {
        if (this.stage) {
            if (e.locked) this.ticker.stop();
            else this.ticker.start();
        }
    }

    /**
     * Изменение размера приложения
     * @param {number} width
     * @param {number} height
     */
    resize(width, height) {
        this.config.size.width = width;
        this.config.size.height = height;

        for (var stage of this.stages) {
            stage.resize(width, height);
        }

        this.layout.size.width = width;
        this.layout.size.height = height;
        this.layout.fitLayout(true);
    }

    addStage() {
        let stage = new Stage(this.layout, this.config, this.layout.bitmapScale);
        this.stages.push(stage);
    }

    get stage() {
        return this.stages[this.stages.length - 1] || null;
    }

    set stage(stage) {

    }

    getStage(ix) {
        return this.stages[ix] || null;
    }

    run() {
        this.layout.showScreens();
        this.ticker.on("tick", this.tick, this);
    }

    tick(e) {
        this._fpsClearTime += e.delta;
        if (this._fpsClearTime >= 1000) {
            this._fpsClearTime -= 1000;
            this._lastFPS = this._fps;
            this._fps = 0;
            this.displayFPS();
        }

        this._fps++;

        for (let stage of this.stages) {
            stage.tick(this.delta);
        }
    }

    get showFPS() {
        return this._showFPS;
    }

    set showFPS(val) {
        this._showFPS = !!val;

        let el = document.getElementById(fpsDivId);

        if (!this._showFPS && el) document.body.removeChild(el);

        if (this._showFPS && !el) {
            el = document.createElement("div");
            el.id = fpsDivId;
            el.style.position = "absolute";
            el.style.left = "0";
            el.style.top = "0";
            el.style.zIndex = 100500;
            el.style.background = "#000";
            el.style.color = "#fff";
            el.style.padding = "3px";
            el.innerHTML = "FPS: 0";
            document.body.appendChild(el);
        }
    }

    displayFPS() {
        if (!this.showFPS) return;

        let el = document.getElementById(fpsDivId);
        if (!el) return;
        el.innerHTML = `[x${this.layout.bitmapScale}] FPS: ${this._lastFPS}`;
    }
}

export default Application;
